import { isEqual } from 'lodash-es';

import { getColumnLabel } from '../../../../utils/get-column-label.js';
import type { DataTableColumnVisibilityState } from '../../../features/ColumnVisibility/column-visibility-types.js';
import type { Column } from '../../../hooks/useTable/types.js';
import type { ColumnPinningState } from '../../../tanstack-table-core-override/src/index.js';

//#region filtering

/**
 * Generates a map of column IDs to their lowercase labels for efficient filtering.
 * @param columns - The columns to generate labels for.
 * @returns A map of column IDs to lowercase labels.
 * @internal
 */
export function generateColumnLabelsMap(
  columns: Column<unknown, unknown>[],
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const col of columns) {
    labels.set(col.id, getColumnLabel(col).toLocaleLowerCase());
  }
  return labels;
}

/**
 * Processes a filter string into an array of lowercase words.
 * @param filterValue - The filter string to process.
 * @returns An array of lowercase words.
 * @internal
 */
export function processFilterValue(filterValue: string): string[] {
  return filterValue.toLocaleLowerCase().split(' ').filter(Boolean);
}

//#endregion

//#region general utils

/**
 * Adds the parent column before adjacent leaf columns.
 * @param columns - the leaf column ids.
 * @remarks The same parent column may appear multiple times in the pinned areas.
 */
export function groupColumns(
  columns: Column<unknown, unknown>[],
): Column<unknown, unknown>[] {
  const groupedColumns: Column<unknown, unknown>[] = [];
  let lastParent: Column<unknown, unknown> | undefined = undefined;
  for (const col of columns) {
    const parent = col.parent;
    // if the current column has no parent, add it and move on to the next
    if (parent === undefined) {
      lastParent = undefined;
      groupedColumns.push(col);
      continue;
    }
    // if the current column belongs to a new parent, start a new group
    if (parent !== lastParent) {
      groupedColumns.push(parent);
      lastParent = parent;
    }
    groupedColumns.push(col);
  }
  return groupedColumns;
}

//#endregion

//#region visibility

/**
 * @returns true if *at least one* of the child columns can be hidden, false otherwise.
 */
export function isParentColumnHideable(
  childColumns: Column<unknown, unknown>[],
): boolean {
  return childColumns.some((c) => c.columnDef.enableHiding);
}

/**
 * Generates an object with the visibility of each column.
 * @param columns - The columns in the table.
 * @param columnVisibility - The visibility of the table columns.
 * @returns an object that contains the column ids as keys with their visibility as values.
 * @internal
 */
export function generateColumnVisibilityObject(
  columns: Column<unknown, unknown>[],
  columnVisibility?: Record<string, boolean>,
): Record<string, boolean> {
  return Object.fromEntries(
    columns.map((column) => [
      column.id,
      columnVisibility
        ? columnVisibility[column.id] ?? true
        : column.getIsVisible(),
    ]),
  );
}

/**
 * Returns the local column visibility of multiple columns used when clicking show/hide all or parent column checkboxes.
 * @param columns - The columns in the table.
 * @param currentVisibility - The current column visibility state in the table.
 * @param localColumnsVisibilityState - The current column visibility state in the settings.
 * @internal
 */
export function getMultipleLocalColumnVisibility(
  columns: Column<unknown, unknown>[],
  currentVisibility: boolean | 'indeterminate',
  localColumnsVisibilityState: Record<string, boolean>,
): Record<string, boolean> {
  const newVisibility = currentVisibility !== true;
  const newState = { ...localColumnsVisibilityState };

  for (const column of columns) {
    // Only update visibility if the column can be hidden
    // This prevents both hiding and unhiding columns with disableColumnHiding: true
    if (column.getCanHide()) {
      newState[column.id] = newVisibility;
    }
  }

  return newState;
}

/**
 * Returns the current checkbox state based on the column visibility state.
 * @param columnVisibilityState - the current column visibility state.
 * @param column - the column to get the visibility state for.
 */
export function getVisibilityCheckboxState(
  columnVisibilityState: Record<string, boolean>,
  column: Column<unknown, unknown>,
): boolean | 'indeterminate' {
  const childColumns = column.columns;
  // if the column has no child columns, return its visibility state
  if (childColumns.length === 0) {
    return columnVisibilityState?.[column.id] ?? false;
  }
  // if the column is a parent column, we need to consider all child columns to determine its visibility state
  const childrenVisibility = generateColumnVisibilityObject(
    childColumns,
    columnVisibilityState,
  );
  return getCombinedVisibilityCheckboxState(childrenVisibility, childColumns);
}

/**
 * Gets the combined checked value for a parent visibility checkbox
 * @param columnVisibilityState - The visibility of the table columns.
 * @param columns - The columns to check whether visibility can be toggled.
 * @returns
 *  - `true` if all columns are visible or disabled,
 *  - `false` if all columns are hidden,
 *  - `indeterminate` otherwise.
 * @internal
 */
export function getCombinedVisibilityCheckboxState(
  columnVisibilityState: Record<string, boolean>,
  columns: Column<unknown, unknown>[],
): boolean | 'indeterminate' {
  let allVisible = true;
  let allHidden = true;

  for (const col of columns) {
    const isVisible = columnVisibilityState[col.id] !== false;
    // skip disabled columns that are not visible
    if (!isVisible && col.getCanHide() === false) {
      continue;
    }
    if (isVisible) {
      allHidden = false;
    } else {
      allVisible = false;
    }
    // Early exit: mixed state detected.
    if (!allVisible && !allHidden) {
      return 'indeterminate';
    }
  }

  if (allVisible) {
    return true;
  }
  if (allHidden) {
    return false;
  }
  return 'indeterminate';
}

/**
 * Determines whether the visibility of one or multiple columns has changed.
 * @param columnVisibilityToReset - The column visibility state that is used to reset.
 * @param localColumnVisibility - The current column visibility state in the settings.
 * @returns `true` if the visibility has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasVisibilityChanged(
  columnVisibilityToReset: DataTableColumnVisibilityState | undefined,
  localColumnVisibility: Record<string, boolean>,
): boolean {
  if (!columnVisibilityToReset) {
    return false;
  }

  return Object.keys(localColumnVisibility).some((key) => {
    // If the key is not present in columnVisibilityToReset, consider it as visible
    if (!(key in columnVisibilityToReset)) {
      return localColumnVisibility[key] !== true;
    }
    return localColumnVisibility[key] !== columnVisibilityToReset[key];
  });
}
//#endregion

//#region ordering
/**
 * Generates a column order that includes parent ids.
 * @param getColumn - the function to get a column by its id.
 * @param columnOrder - the column order with leaf columns only.
 * @returns the column order including parent ids and a list of unique parent ids.
 */
export function generateColumnOrderWithParentIds(
  getColumn: (columnId: string) => Column<unknown, unknown> | undefined,
  columnOrder: string[],
): { columnOrderWithParents: string[]; uniqueParentIds: string[] } {
  const columnOrderWithParents: string[] = [];
  const uniqueParentIds = new Set<string>();

  for (const columnId of columnOrder) {
    const column = getColumn(columnId);
    const parentId = column?.parent?.id;

    // insert parent id before child if not already added
    if (parentId && !uniqueParentIds.has(parentId)) {
      columnOrderWithParents.push(parentId);
      uniqueParentIds.add(parentId);
    }
    columnOrderWithParents.push(columnId);
  }
  return {
    columnOrderWithParents,
    uniqueParentIds: Array.from(uniqueParentIds),
  };
}

/**
 * Determines whether the order of one or multiple columns has changed.
 * @param columnOrderToReset - The column order state that is used to reset.
 * @param localColumnOrder - The current column order state in the settings.
 * @returns `true` if the order has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasColumnOrderChanged(
  columnOrderToReset: string[] | undefined,
  localColumnOrder: string[],
): boolean {
  if (!columnOrderToReset) {
    return false;
  }
  return !isEqual(columnOrderToReset, localColumnOrder);
}

//#endregion

//#region pinning

/**
 * Determines whether the pinned state of one or multiple columns has changed.
 * @param columnPinnedToReset - The column pinning state that is used to reset.
 * @param localColumnsPinned - The current column pinning state in the settings.
 * @returns `true` if the pinned state has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasColumnPinningChanged(
  columnPinnedToReset: ColumnPinningState | undefined,
  localColumnsPinned: ColumnPinningState,
): boolean {
  if (!columnPinnedToReset) {
    return false;
  }
  return (
    !isEqual(columnPinnedToReset.left, localColumnsPinned.left) ||
    !isEqual(columnPinnedToReset.right, localColumnsPinned.right)
  );
}

//#endregion
